/*
 * Invoice tax math in one place (QA batch 17), used by the invoice form, the
 * invoice page and the PDF.
 *
 * BILL-039: each line keeps its OWN tax code (InvoiceItems.TaxCodeId). Before,
 * the form had one tax code for the whole invoice: changing one line's "Sales
 * tax" box changed every line, and the page/PDF used the first line's code.
 * Each line's tax = its share of the amount after the discount (the discount is
 * spread over the lines by amount) x its code's rates; the Summary lists each
 * tax (name + rate) once with the sum over the lines.
 *
 * DATA-001: an invoice keeps the total it was saved with. When a tax code's
 * rates were changed later (or the invoice came from QuickBooks with its own
 * tax), today's rates no longer give that total (invoice 1031: $5,000 + rates
 * now 10% + 20% = $6,500, saved total $6,784.70). `savedTaxNote` then shows the
 * tax as saved (total - subtotal + discount) instead of a sum that doesn't add up.
 */

export type TaxRateLike = { id?: string; taxName?: string; taxRate: number };
export type TaxCodeLike = {
  id?: string;
  taxCodeName?: string;
  taxRates?: TaxRateLike[] | null;
};
export type TaxedLine = { amount: number; taxCodeId?: string | null };

export type TaxRow = {
  key: string;
  taxName: string;
  taxRate: number;
  amount: number;
};

export type InvoiceTaxSummary = {
  subtotal: number;
  discountValue: number;
  /** Amount after the discount (never below 0). */
  taxableAmount: number;
  /** Amounts the taxes are computed on (without tax when "Inclusive of Tax"). */
  taxBaseAmount: number;
  rows: TaxRow[];
  totalTax: number;
  total: number;
};

const sumRates = (code?: TaxCodeLike | null) =>
  (code?.taxRates || []).reduce((sum, rate) => sum + (rate.taxRate || 0), 0);

export const findTaxCode = (
  codes: TaxCodeLike[] | null | undefined,
  id?: string | null,
) =>
  id
    ? (codes || []).find(
        (code) => code.id?.toLowerCase() === String(id).toLowerCase(),
      ) ?? null
    : null;

export const discountAmount = (
  subtotal: number,
  discount: number,
  type: "$" | "%" | null,
) => {
  if (type === "$") return discount || 0;
  if (type === "%") return (subtotal * (discount || 0)) / 100;
  return 0;
};

export const invoiceTaxSummary = (params: {
  items: TaxedLine[];
  taxCodes: TaxCodeLike[] | null | undefined;
  discountValue: number;
  isIncludeTax: boolean;
  /** Code for lines without their own (older invoices). */
  fallbackTaxCodeId?: string | null;
}): InvoiceTaxSummary => {
  const { items, taxCodes, isIncludeTax, fallbackTaxCodeId } = params;
  const subtotal = items.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );
  const discountValue = Number(params.discountValue) || 0;
  const taxableAmount = Math.max(subtotal - discountValue, 0);
  // Part of each line left after the discount.
  const keep = subtotal > 0 ? taxableAmount / subtotal : 1;

  const rows = new Map<string, TaxRow>();
  let taxBaseAmount = 0;
  items.forEach((item) => {
    const lineTaxable = (Number(item.amount) || 0) * keep;
    const code =
      findTaxCode(taxCodes, item.taxCodeId) ??
      findTaxCode(taxCodes, fallbackTaxCodeId);
    const rate = sumRates(code);
    const base =
      isIncludeTax && rate > 0 ? lineTaxable / (1 + rate / 100) : lineTaxable;
    taxBaseAmount += base;
    (code?.taxRates || []).forEach((taxRate) => {
      const key = `${(taxRate.taxName || "").trim().toLowerCase()}|${
        taxRate.taxRate || 0
      }`;
      const row = rows.get(key) ?? {
        key,
        taxName: taxRate.taxName || code?.taxCodeName || "Tax",
        taxRate: taxRate.taxRate || 0,
        amount: 0,
      };
      row.amount += (base * (taxRate.taxRate || 0)) / 100;
      rows.set(key, row);
    });
  });

  const list = Array.from(rows.values());
  const totalTax = list.reduce((sum, row) => sum + row.amount, 0);
  return {
    subtotal,
    discountValue,
    taxableAmount,
    taxBaseAmount,
    rows: list,
    totalTax,
    total: isIncludeTax ? taxableAmount : taxableAmount + totalTax,
  };
};

/**
 * The tax as the invoice was saved, when today's rates don't give its saved
 * total (more than a cent apart, not "Inclusive of Tax"). null = rates agree.
 */
export const savedTaxNote = (
  summary: InvoiceTaxSummary,
  savedTotal: number | null | undefined,
  isIncludeTax: boolean,
): { tax: number } | null => {
  if (isIncludeTax || savedTotal == null) return null;
  if (Math.abs(summary.total - savedTotal) <= 0.01) return null;
  return { tax: savedTotal - summary.taxableAmount };
};

export const SAVED_TAX_HINT =
  "The tax code's rates changed after this invoice was saved (or the tax came from QuickBooks), so the invoice keeps the tax and total it was saved with.";
